/**
 * Derives active filter chip descriptors from modal filter state
 *
 * Produces an array of chip descriptors (key + label) for each active
 * filter category, used to render dismissible chips below the search bar
 * in the Add Player to List modal. All option lookups come from the
 * FilterState itself (backed by dedicated list queries), so labels
 * are stable regardless of the current search results.
 * @module features/scouting/utils/deriveActiveFilterChips
 */

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_CUSTOM_STAT_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
  isRangeActive,
} from "@/constants/filterDefaults";

import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import type { FilterState } from "@/hooks/useFilterState";

/** Descriptor for an active filter chip shown below the search bar */
export interface ChipDescriptor {
  readonly key: string;
  readonly label: string;
}

/**
 * Builds a Record mapping option IDs to display labels
 * @param options - Filter options to index
 * @returns Lookup record from id to label
 */
const buildOptionLookup = (
  options: readonly FilterOption[]
): Record<string, string> =>
  options.reduce(
    (acc, o) => ({ ...acc, [o.id]: o.label }),
    {} as Record<string, string>
  );

/**
 * Formats a chip label showing category name and selected values
 * @param name - Category display name
 * @param ids - Selected IDs
 * @param lookup - ID-to-label lookup
 * @returns "Name: A, B" when two or fewer selected, "Name (N)" otherwise
 */
const formatChipLabel = (
  name: string,
  ids: readonly string[],
  lookup: Record<string, string>
): string =>
  ids.length <= 2
    ? `${name}: ${ids.map(id => lookup[id] ?? id).join(", ")}`
    : `${name} (${ids.length})`;

/**
 * Conditionally creates a chip descriptor array
 * @param condition - Whether to include the chip
 * @param key - Chip identifier key (matches chipRemoveHandlers keys)
 * @param label - Display label for the chip
 * @returns Single-element array when active, empty otherwise
 */
const chipIf = (
  condition: boolean,
  key: string,
  label: string
): readonly ChipDescriptor[] => (condition ? [{ key, label }] : []);

/**
 * Derives active filter chip descriptors from current filter state.
 * Each active filter category produces one chip with a human-readable label.
 * All option lookups come from the state itself (backed by dedicated queries),
 * so labels are stable regardless of search results.
 * @param state - Modal filter state with all selected IDs, ranges, and option lists
 * @returns Chip descriptors for all active filters
 */
const deriveActiveFilterChips = (
  state: FilterState
): readonly ChipDescriptor[] => {
  const posLookup = buildOptionLookup(state.positionOptions);
  const clubLookup = buildOptionLookup(state.clubOptions);
  const roleLookup = buildOptionLookup(state.roleOptions);
  const scoutLookup = buildOptionLookup(state.scoutOptions);
  const leagueLookup = buildOptionLookup(state.leagueOptions);
  const competitionLookup = buildOptionLookup(state.competitionOptions);
  const nationalityLookup = buildOptionLookup(state.nationalityOptions);

  return [
    ...chipIf(
      state.selectedRoleArchetypes.length > 0,
      "roles",
      formatChipLabel("Roles", state.selectedRoleArchetypes, roleLookup)
    ),
    ...chipIf(
      state.selectedPositionIds.length > 0,
      "positions",
      formatChipLabel("Position", state.selectedPositionIds, posLookup)
    ),
    ...chipIf(
      state.selectedClubIds.length > 0,
      "clubs",
      formatChipLabel("Club", state.selectedClubIds, clubLookup)
    ),
    ...chipIf(
      state.selectedScoutIds.length > 0,
      "scouts",
      formatChipLabel("Scout", state.selectedScoutIds, scoutLookup)
    ),
    ...chipIf(
      state.selectedNationalityIds.length > 0,
      "nationalities",
      formatChipLabel(
        "Nationality",
        state.selectedNationalityIds,
        nationalityLookup
      )
    ),
    ...chipIf(
      state.selectedLeagueIds.length > 0,
      "leagues",
      formatChipLabel("League", state.selectedLeagueIds, leagueLookup)
    ),
    ...chipIf(
      state.selectedCompetitionIds.length > 0,
      "competitions",
      formatChipLabel(
        "Cup competitions",
        state.selectedCompetitionIds,
        competitionLookup
      )
    ),
    ...chipIf(
      isRangeActive(state.ageRange, DEFAULT_AGE_RANGE),
      "age",
      `Age: ${state.ageRange[0]}\u2013${state.ageRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.contractMonthsRange, DEFAULT_CONTRACT_MONTHS_RANGE),
      "contractMonths",
      `Contract: ${state.contractMonthsRange[0]}\u2013${state.contractMonthsRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.valuationRange, DEFAULT_VALUATION_RANGE),
      "valuation",
      `Cost: ${state.valuationRange[0]}\u2013${state.valuationRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.gprRange, DEFAULT_SCORE_RANGE),
      "gpr",
      `GPR: ${state.gprRange[0]}\u2013${state.gprRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.fitScoreRange, DEFAULT_SCORE_RANGE),
      "fitScore",
      `Player Fit: ${state.fitScoreRange[0]}\u2013${state.fitScoreRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.teamStyleFitRange, DEFAULT_SCORE_RANGE),
      "teamStyleFit",
      `Team Fit: ${state.teamStyleFitRange[0]}\u2013${state.teamStyleFitRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.carryingRange, DEFAULT_SCORE_RANGE),
      "carrying",
      `Carrying: ${state.carryingRange[0]}\u2013${state.carryingRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.dribblingRange, DEFAULT_SCORE_RANGE),
      "dribbling",
      `Dribbling: ${state.dribblingRange[0]}\u2013${state.dribblingRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.passingRange, DEFAULT_SCORE_RANGE),
      "passing",
      `Passing: ${state.passingRange[0]}\u2013${state.passingRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.shootingRange, DEFAULT_SCORE_RANGE),
      "shooting",
      `Shooting: ${state.shootingRange[0]}\u2013${state.shootingRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.gkPassingRange, DEFAULT_SCORE_RANGE),
      "gkPassing",
      `GK Passing: ${state.gkPassingRange[0]}\u2013${state.gkPassingRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.gkShotStoppingRange, DEFAULT_SCORE_RANGE),
      "gkShotStopping",
      `Shot Stopping: ${state.gkShotStoppingRange[0]}\u2013${state.gkShotStoppingRange[1]}`
    ),
    ...chipIf(
      isRangeActive(state.gkPositioningRange, DEFAULT_SCORE_RANGE),
      "gkPositioning",
      `Positioning: ${state.gkPositioningRange[0]}\u2013${state.gkPositioningRange[1]}`
    ),
    ...Object.entries(state.customStatRanges)
      .filter(([, range]) =>
        isRangeActive(range as [number, number], DEFAULT_CUSTOM_STAT_RANGE)
      )
      .map(([name, range]) => ({
        key: `customStat_${name}`,
        label: `${name}: ${range[0]}\u2013${range[1]}`,
      })),
  ];
};

export default deriveActiveFilterChips;
